// ModuleChips — the optional-module (entitlement) row: which add-ons a project
// has (ADR-044's `has_*` flags, rendered). Promoted out of the platform-projects
// page folder at its second consumer, the project-admin settings surface.
//
// The two audiences differ in ONE way, so that is the one prop:
//   sysadmin  → `showOff={false}` (default): enabled modules only; an empty set
//               renders `emptyLabel`. The sysadmin already has the toggle UI, so
//               an off module is not news.
//   project_admin → `showOff`: off modules stay visible but recede (dashed,
//               muted). An admin who cannot toggle needs to SEE what exists and
//               is not switched on — that is the honest read of "your plan" —
//               without the row becoming an upsell.
//
// Namespace-free: items arrive with their labels resolved, so each consumer
// supplies strings from its own i18n namespace.

import type { Icon } from '../ui/icons.js';

export interface ModuleChip {
  /** Stable key — the React key; never user-facing. */
  key: string;
  /** Already-i18n'd module name. */
  label: string;
  icon: Icon;
  on: boolean;
}

export interface ModuleChipsProps {
  items: ModuleChip[];
  /** Render off modules too (dashed + muted). Default: enabled only. */
  showOff?: boolean;
  /** Shown when nothing would render (already i18n'd). */
  emptyLabel?: string;
  /** Suffix on an off chip, e.g. "off" (already i18n'd). Only used with showOff. */
  offLabel?: string;
  className?: string;
}

export function ModuleChips({ items, showOff = false, emptyLabel, offLabel, className = '' }: ModuleChipsProps) {
  const shown = showOff ? items : items.filter((m) => m.on);
  if (shown.length === 0) {
    return <span className="text-sm text-[var(--color-text-disabled)]">{emptyLabel}</span>;
  }
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {shown.map(({ key, label, icon: Icon, on }) => (
        <span
          key={key}
          className={
            on
              ? 'inline-flex items-center gap-1.5 rounded-md bg-[var(--color-primary-display)]/12 px-2 py-1 text-sm text-[var(--color-text-primary)]'
              : 'inline-flex items-center gap-1.5 rounded-md border border-dashed border-[var(--color-border-strong)] px-2 py-1 text-sm text-[var(--color-text-muted)]'
          }
        >
          <Icon size={16} aria-hidden className={on ? 'text-[var(--color-primary-display)]' : undefined} />
          {label}
          {!on && offLabel && <span className="text-xs">· {offLabel}</span>}
        </span>
      ))}
    </div>
  );
}
